import type { IndicatorRegistryEntry } from "lightweight-charts-indicators"
import type { IndicatorRenderer } from "lightweight-charts-indicators/render"
import { useEffect, useState } from "react"

// The indicator catalogue (117 TradingView built-ins, @trader/indicators) and
// the renderer load on first use, so charts without indicators stay light.

export interface IndicatorModules {
  readonly catalog: ReadonlyArray<IndicatorRegistryEntry>
  readonly byId: ReadonlyMap<string, IndicatorRegistryEntry>
  readonly IndicatorRenderer: typeof IndicatorRenderer
}

let pending: Promise<IndicatorModules> | null = null

export const loadIndicatorModules = (): Promise<IndicatorModules> => {
  if (pending !== null) return pending
  const loading = Promise.all([import("@trader/indicators"), import("lightweight-charts-indicators/render")]).then(
    ([indicators, render]) => ({
      catalog: indicators.standardIndicators,
      byId: new Map(indicators.standardIndicators.map((entry) => [entry.id, entry])),
      IndicatorRenderer: render.IndicatorRenderer
    })
  )
  // A failed download (e.g. a release replaced the chunk) is retried on the next use.
  loading.catch(() => {
    if (pending === loading) pending = null
  })
  pending = loading
  return loading
}

/** The modules once loaded; starts loading when `needed` becomes true. */
export const useIndicatorModules = (needed: boolean): IndicatorModules | null => {
  const [modules, setModules] = useState<IndicatorModules | null>(null)
  useEffect(() => {
    if (!needed || modules !== null) return
    let current = true
    loadIndicatorModules().then(
      (loaded) => current && setModules(loaded),
      () => {
        // Stays without indicators; the next mount or `needed` change retries.
      }
    )
    return () => {
      current = false
    }
  }, [needed, modules])
  return modules
}
